import { ref } from 'vue'
import * as slaService from '@/services/slaService'
import type { SLAProfile, SLAFluxoMap } from '@/services/slaService'

export type { SLAProfile, SLAFluxoMap }

const profiles   = ref<SLAProfile[]>([])
const fluxoMap    = ref<SLAFluxoMap>({})
const slaLoaded   = ref(false)
const slaLoading  = ref(false)

// ── Public API ────────────────────────────────────────────
export function useSLA() {

  async function loadProfiles() {
    if (slaLoaded.value || slaLoading.value) return
    slaLoading.value = true
    try {
      const { profiles: list, fluxoMap: map } = await slaService.fetchSLAProfiles()
      profiles.value = list
      fluxoMap.value = map
      slaLoaded.value = true
    } catch (e) {
      console.error('[useSLA] loadProfiles:', e)
    } finally {
      slaLoading.value = false
    }
  }

  async function upsertProfile(p: SLAProfile) {
    const isNew = !profiles.value.find(x => x.id === p.id)
    try {
      await slaService.createOrUpdateSLAProfile(p, isNew)
      const i = profiles.value.findIndex(x => x.id === p.id)
      if (i >= 0) profiles.value[i] = p
      else profiles.value.push(p)
    } catch (e) {
      console.error('[useSLA] upsertProfile:', e)
      throw e
    }
  }

  async function deleteProfile(id: string) {
    try {
      await slaService.deleteSLAProfile(id)
      profiles.value = profiles.value.filter(p => p.id !== id)
      for (const fid of Object.keys(fluxoMap.value)) {
        if (fluxoMap.value[fid] === id) delete fluxoMap.value[fid]
      }
    } catch (e) {
      console.error('[useSLA] deleteProfile:', e)
      throw e
    }
  }

  async function setFluxoProfile(fluxoId: string, profileId: string | null) {
    try {
      await slaService.setSLAFluxoProfile(fluxoId, profileId)
      if (profileId) fluxoMap.value[fluxoId] = profileId
      else delete fluxoMap.value[fluxoId]
    } catch (e) {
      console.error('[useSLA] setFluxoProfile:', e)
      throw e
    }
  }

  function getProfileForFluxo(fluxoId: number | string | null): SLAProfile | null {
    if (fluxoId === null || fluxoId === undefined) return null
    const pid = fluxoMap.value[String(fluxoId)]
    if (!pid) return null
    return profiles.value.find(p => p.id === pid) || null
  }

  return {
    profiles,
    fluxoMap,
    slaLoaded,
    slaLoading,
    loadProfiles,
    upsertProfile,
    deleteProfile,
    setFluxoProfile,
    getProfileForFluxo,
  }
}
